import { EntityChip } from '@autho/ui';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';
import { useEntities } from '../lib/api.ts';
import { useT } from '../lib/preferences.tsx';
import { useTenant } from '../lib/tenant.tsx';
import { screensFor } from './menu.ts';

/**
 * Design Principles #9: ⌘K reaches every entity and screen — **of the open profile** (R4). The screens are the menu's,
 * in the menu's order, read from the same table, so the palette cannot offer a screen the menu hides and the route
 * guard would turn away. Entities are an Org's; a person's own profile has none, and is offered no empty group.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  const { tenantId, kind, chatOpen } = useTenant();
  // A company's entities are asked for in a company only (R3); the list below is drawn there alone.
  const entities = useEntities(tenantId, kind === 'org');
  const navigate = useNavigate();

  const go = (to: string, params?: Record<string, string>) => {
    onOpenChange(false);
    void navigate({ to, ...(params ? { params } : {}), search: (prev) => prev });
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20" />
        <Dialog.Content className="fixed top-24 left-1/2 z-50 w-full max-w-lg -translate-x-1/2 overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
          <Dialog.Title className="sr-only">{t('palette.placeholder')}</Dialog.Title>
          <Dialog.Description className="sr-only">{t('palette.placeholder')}</Dialog.Description>
          <Command label={t('palette.placeholder')}>
            <Command.Input
              placeholder={t('palette.placeholder')}
              className="h-11 w-full border-b border-border bg-transparent px-4 text-sm outline-none"
            />
            <Command.List className="max-h-80 overflow-y-auto p-1">
              <Command.Empty className="px-3 py-2 text-sm text-muted">
                {t('palette.empty')}
              </Command.Empty>
              <Command.Group
                heading={t('palette.group.screens')}
                className="text-xs text-muted [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1"
              >
                {screensFor(kind, { chat: chatOpen }).map((screen) => (
                  <Command.Item
                    key={screen.to}
                    value={t(screen.label)}
                    onSelect={() => go(screen.to)}
                    className="flex h-8 cursor-pointer items-center rounded px-2 text-sm text-text data-[selected=true]:bg-hover"
                  >
                    {t(screen.label)}
                  </Command.Item>
                ))}
              </Command.Group>
              {kind === 'org' && (
                <Command.Group
                  heading={t('palette.group.entities')}
                  className="text-xs text-muted [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1"
                >
                  {(entities.data ?? []).map((entity) => (
                    <Command.Item
                      key={entity.id}
                      value={`${entity.name} ${entity.orgnr}`}
                      onSelect={() => go('/structure/$entityId', { entityId: entity.id })}
                      className="flex h-8 cursor-pointer items-center rounded px-2 text-sm text-text data-[selected=true]:bg-hover"
                    >
                      <EntityChip id={entity.id} name={entity.name} orgnr={entity.orgnr} />
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
